import { useEffect, useState } from 'react';

/**
 * Which page section the reader is currently in: the last one whose top edge
 * has passed `offset` pixels from the top of the viewport. Returns null while
 * above the first section (e.g. still on the hero).
 *
 * Reads layout on scroll, throttled to animation frames. Unlike the hero's
 * scroll-linked motion, this keeps working under `prefers-reduced-motion`, since it drives
 * navigation state rather than decoration.
 */
export function useActiveSection(ids: readonly string[], offset = 120): string | null {
  const [active, setActive] = useState<string | null>(null);
  const key = ids.join('|');

  useEffect(() => {
    const list = key ? key.split('|') : [];
    let frame = 0;

    const compute = () => {
      frame = 0;
      // The current section is the one whose top edge has passed `offset` and
      // is closest to it, i.e. the lowest one on the page that has been
      // reached. Going by position (not list order) means the ids can be
      // given in any order, such as the order they appear in a nav bar.
      let current: string | null = null;
      let best = -Infinity;
      for (const id of list) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= offset && top > best) {
          best = top;
          current = id;
        }
      }
      setActive(current);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(compute);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [key, offset]);

  return active;
}
